import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Download, Search, CheckCircle2, LogOut, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAdminSession } from "@/hooks/useAdminSession";
import { AdminLoginForm } from "@/components/shared/AdminLoginForm";
import { AdminChangePasswordModal } from "@/components/shared/AdminChangePasswordModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CSV_COLUMNS = [
  ["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["state", "State"],
  ["cdl_class", "CDL Class"], ["experience_years", "Experience (yrs)"], ["listed", "Listed"],
  ["profile_complete", "Complete"], ["source", "Source"], ["created_at", "Created At"],
];

const toCSV = (rows) => {
  const lines = rows.map((r) => CSV_COLUMNS.map(([key]) => {
    const v = typeof r[key] === "boolean" ? (r[key] ? "Yes" : "No") : r[key];
    return `"${String(v ?? "").replace(/"/g, '""')}"`;
  }).join(","));
  return [CSV_COLUMNS.map(([, label]) => label).join(","), ...lines].join("\n");
};

const REQUEST_STATUSES = ["pending", "contacted", "closed"];

export default function AdminDrivers() {
  const { admin, checking, login, logout } = useAdminSession();
  const [drivers, setDrivers] = useState(null);
  const [requests, setRequests] = useState(null);
  const [query, setQuery] = useState("");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [driversRes, requestsRes] = await Promise.all([axios.get(`${API}/drivers/admin`), axios.get(`${API}/drivers/requests`)]);
      setDrivers(driversRes.data);
      setRequests(requestsRes.data.requests);
    } catch {
      setDrivers(null);
      setRequests(null);
    }
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const filteredDrivers = useMemo(() => {
    if (!drivers) return [];
    const q = query.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => [d.name, d.email, d.state, d.city].some((v) => v?.toLowerCase().includes(q)));
  }, [drivers, query]);

  const updateRequestStatus = async (requestId, status) => {
    setUpdatingId(requestId);
    try {
      await axios.patch(`${API}/drivers/requests/${requestId}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, status } : r)));
      toast.success("Request status updated.");
    } catch {
      toast.error("Couldn't update status. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const exportCSV = () => {
    const blob = new Blob([toCSV(filteredDrivers)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relayrigs-drivers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) return null;
  if (!admin) return <AdminLoginForm title="Driver Directory Admin" subtitle="Sign in to manage driver profiles and requests." onLogin={login} />;
  if (!drivers) return <div data-testid="admin-drivers-loading" className="min-h-screen bg-rr-surface flex items-center justify-center"><Loader2 size={24} className="animate-spin text-rr-orange" /></div>;

  return (
    <div data-testid="admin-drivers-dashboard" className="min-h-screen bg-rr-surface py-14 px-5 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-rr-navy">Driver Directory</h1>
            <p className="text-sm text-rr-muted mt-1">{drivers.length} total driver profile{drivers.length === 1 ? "" : "s"} · {requests?.length || 0} request{requests?.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setChangePasswordOpen(true)} data-testid="admin-drivers-change-password-button" className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"><KeyRound size={14} /> Change Password</button>
            <button onClick={load} data-testid="admin-drivers-refresh-button" className="flex items-center gap-2 text-sm font-bold text-rr-navy border border-rr-line rounded-full px-4 py-2 hover:border-rr-orange transition-colors bg-white"><RefreshCw size={14} /> Refresh</button>
            <button onClick={exportCSV} data-testid="admin-drivers-export-button" className="flex items-center gap-2 text-sm font-bold text-white bg-rr-orange rounded-full px-4 py-2 hover:bg-rr-navy transition-colors"><Download size={14} /> Export CSV</button>
            <button onClick={logout} data-testid="admin-drivers-logout-button" className="flex items-center gap-2 text-sm font-bold text-rr-muted border border-rr-line rounded-full px-4 py-2 hover:border-rr-red hover:text-rr-red transition-colors bg-white"><LogOut size={14} /> Sign Out</button>
          </div>
        </div>

        <div className="relative max-w-sm mb-4">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-rr-muted" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, city, state..." data-testid="admin-drivers-search-input" className="w-full border border-rr-line rounded-full pl-10 pr-4 py-2.5 text-sm bg-white focus:outline-none focus:border-rr-orange" />
        </div>

        <div className="bg-white rounded-2xl border border-rr-line overflow-x-auto mb-10">
          <table data-testid="admin-drivers-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-rr-muted border-b border-rr-line">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">CDL</th><th className="px-4 py-3">Exp.</th><th className="px-4 py-3">Listed</th><th className="px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map((d) => (
                <tr key={d.id} data-testid={`admin-drivers-row-${d.id}`} className="border-b border-rr-line last:border-0">
                  <td className="px-4 py-3 font-semibold text-rr-navy">{d.name}</td>
                  <td className="px-4 py-3 text-rr-muted">{d.email}</td>
                  <td className="px-4 py-3 text-rr-muted">{[d.city, d.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{d.cdl_class || "—"}</td>
                  <td className="px-4 py-3 text-rr-muted">{d.experience_years ?? "—"}</td>
                  <td className="px-4 py-3">{d.listed && d.profile_complete ? <span className="inline-flex items-center gap-1.5 text-rr-green font-bold text-xs"><CheckCircle2 size={13} /> Listed</span> : <span className="text-rr-muted text-xs font-bold">Not listed</span>}</td>
                  <td className="px-4 py-3 text-rr-muted text-xs capitalize">{d.source}</td>
                </tr>
              ))}
              {filteredDrivers.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-rr-muted">No matching drivers.</td></tr>}
            </tbody>
          </table>
        </div>

        <h2 className="font-heading text-xl font-bold text-rr-navy mb-4">Driver Requests</h2>
        <div className="bg-white rounded-2xl border border-rr-line overflow-x-auto">
          <table data-testid="admin-driver-requests-table" className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wide text-rr-muted border-b border-rr-line">
                <th className="px-4 py-3">Driver</th><th className="px-4 py-3">Requester</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(requests || []).map((r) => (
                <tr key={r.id} data-testid={`admin-driver-request-row-${r.id}`} className="border-b border-rr-line last:border-0">
                  <td className="px-4 py-3 font-semibold text-rr-navy">{r.driver_name}</td>
                  <td className="px-4 py-3 text-rr-muted">{r.requester_name}</td>
                  <td className="px-4 py-3 text-rr-muted">{r.requester_email}{r.requester_phone ? ` · ${r.requester_phone}` : ""}</td>
                  <td className="px-4 py-3 text-rr-muted max-w-xs truncate" title={r.message}>{r.message || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      value={r.status}
                      disabled={updatingId === r.id}
                      onChange={(e) => updateRequestStatus(r.id, e.target.value)}
                      data-testid={`admin-driver-request-status-${r.id}`}
                      className="border border-rr-line rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-rr-orange capitalize"
                    >
                      {REQUEST_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </td>
                </tr>
              ))}
              {(!requests || requests.length === 0) && <tr><td colSpan={5} className="px-4 py-8 text-center text-rr-muted">No driver requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <AdminChangePasswordModal open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </div>
  );
}
